
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Calendar, 
  Clock, 
  FileText,
  ArrowUpDown,
  Filter,
  Search,
  User,
  Briefcase,
  CheckCircle,
  XCircle,
  AlertCircle,
  Trash2,
} from "lucide-react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Link, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

interface Booking {
  id: number;
  service_id: number;
  customer_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  note: string;
  date: string;
  start_time: string;
  end_time: string;
  status: string;
  created_at: string;
  service_name?: string;
}

interface FormattedDateTime {
  date: string;
  time: string;
}

type SortColumn = 'date' | 'service_name' | 'customer_name' | 'status' | 'created_at';
type SortDirection = 'asc' | 'desc';
type StatusFilter = 'all' | 'new' | 'accepted' | 'cancelled' | 'declined' | 'completed' | 'blocked' | 'travel' | 'removed by admin';

const ITEMS_PER_PAGE = 10;

const ManageBookings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<SortColumn>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [serviceFilter, setServiceFilter] = useState<number | 'all'>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [serviceSearchQuery, setServiceSearchQuery] = useState<string>('');
  
  // Use useState to toggle service dropdown visibility
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-bookings", sortColumn, sortDirection, statusFilter, serviceFilter, customerFilter],
    queryFn: async () => {
      let query = supabase
        .from("bookings")
        .select(`
          *,
          services(name)
        `, { count: 'exact' });
      
      // Apply status filter if not 'all'
      if (statusFilter !== 'all') {
        // Handle special case for 'accepted' filter
        if (statusFilter === 'accepted') {
          query = query.eq('status', 'accepted');
        } else {
          query = query.eq('status', statusFilter);
        }
      }
      
      // Apply service filter if not 'all'
      if (serviceFilter !== 'all') {
        query = query.eq('service_id', serviceFilter);
      }

      // Apply customer name filter if provided
      if (customerFilter) {
        query = query.ilike('customer_name', `%${customerFilter}%`);
      }
      
      // Apply sorting
      query = query.order(sortColumn, { ascending: sortDirection === 'asc' });
      
      const { data, error, count } = await query;
      
      if (error) throw error;

      const formattedData = data.map((booking: any) => ({
        ...booking,
        service_name: booking.services?.name || "Unknown Service",
      })) as Booking[];

      return { bookings: formattedData, total: count || 0 };
    },
  });

  // Fetch all services for the service filter dropdown
  const { data: servicesData } = useQuery({
    queryKey: ["services-for-filter"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("services")
        .select("id, name");
      
      if (error) throw error;
      return data;
    },
  });

  // Filter services based on search query
  const filteredServices = servicesData ? 
    servicesData.filter(service => 
      service.name.toLowerCase().includes(serviceSearchQuery.toLowerCase())
    ) : [];

  const bookings = data?.bookings || [];
  const totalItems = data?.total || 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  
  // Calculate the current page's data
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentBookings = bookings.slice(indexOfFirstItem, indexOfLastItem);

  const updateBookingStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const { error } = await supabase
        .from("bookings")
        .update({ status })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-bookings"] });
      toast({ title: "Success", description: "Booking status updated successfully" });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const removeBookingMutation = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase
        .from("bookings")
        .update({ 
          status: 'removed by admin',
          removed_at: new Date().toISOString()
        })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-bookings"] });
      toast({ title: "Success", description: "Booking removed successfully" });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const handleViewDetails = (booking: Booking) => {
    navigate(`/booking/${booking.id}/details`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const handleStatusFilterChange = (status: StatusFilter) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleServiceFilterChange = (serviceId: number | 'all') => {
    setServiceFilter(serviceId);
    setCurrentPage(1); // Reset to first page when filter changes
    setIsServiceDropdownOpen(false); // Close dropdown after selection
  };

  const handleCustomerFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomerFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const SortableHeader = ({ column, children }: { column: SortColumn, children: React.ReactNode }) => (
    <TableHead>
      <button 
        className="flex items-center text-left font-medium text-muted-foreground"
        onClick={() => handleSort(column)}
      >
        {children}
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </button>
    </TableHead>
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "confirmed":
      case "accepted":
        return (
          <span className="flex items-center text-green-600 bg-green-50 px-2 py-1 rounded-full text-xs">
            <CheckCircle className="h-3 w-3 mr-1" /> {status}
          </span>
        );
      case "cancelled":
      case "rejected":
        return (
          <span className="flex items-center text-red-600 bg-red-50 px-2 py-1 rounded-full text-xs">
            <XCircle className="h-3 w-3 mr-1" /> {status}
          </span>
        );
      case "blocked":
        return (
          <span className="flex items-center text-gray-600 bg-gray-50 px-2 py-1 rounded-full text-xs">
            <AlertCircle className="h-3 w-3 mr-1" /> {status}
          </span>
        );
      case "travel":
        return (
          <span className="flex items-center text-blue-600 bg-blue-50 px-2 py-1 rounded-full text-xs">
            <Clock className="h-3 w-3 mr-1" /> {status}
          </span>
        );
      case "removed by admin":
        return (
          <span className="flex items-center text-gray-600 bg-gray-100 px-2 py-1 rounded-full text-xs">
            <Trash2 className="h-3 w-3 mr-1" /> {status}
          </span>
        );
      case "pending":
      default:
        return (
          <span className="flex items-center text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full text-xs">
            <AlertCircle className="h-3 w-3 mr-1" /> {status}
          </span>
        );
    }
  };

  const formatDateTime = (date: string | null, time: string | null): FormattedDateTime => {
    try {
      // If time exists but date doesn't, use the date from the time string for both
      if (!date && time) {
        const timeObj = new Date(time);
        return {
          date: format(timeObj, "MMM dd, yyyy"),
          time: format(timeObj, "h:mm a")
        };
      }
      
      // Original logic if both exist
      if (date && time) {
        const dateObj = new Date(date);
        const timeString = time.split("T")[1] ? time : `${date}T${time}`;
        const timeObj = new Date(timeString);
        
        return {
          date: format(dateObj, "MMM dd, yyyy"),
          time: format(timeObj, "h:mm a")
        };
      }
      
      // Fallback if neither exists
      return { date: "N/A", time: "N/A" };
    } catch (error) {
      console.error("Error formatting date/time:", error);
      return {
        date: "Invalid date",
        time: "Invalid time"
      };
    }
  };

  // Format created_at timestamp
  const formatCreatedAt = (timestamp: string): string => {
    if (!timestamp) return "N/A";
    
    try {
      const date = parseISO(timestamp);
      return format(date, "MMM d, yyyy h:mm a");
    } catch (error) {
      return "Invalid date";
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxPageButtons = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <Pagination className="mt-4">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious 
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
            />
          </PaginationItem>

          {startPage > 1 && (
            <>
              <PaginationItem>
                <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
              </PaginationItem>
              {startPage > 2 && (
                <PaginationItem>
                  <span className="flex h-9 w-9 items-center justify-center">...</span>
                </PaginationItem>
              )}
            </>
          )}

          {pageNumbers.map(number => (
            <PaginationItem key={number}>
              <PaginationLink 
                isActive={currentPage === number}
                onClick={() => handlePageChange(number)}
              >
                {number}
              </PaginationLink>
            </PaginationItem>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <PaginationItem>
                  <span className="flex h-9 w-9 items-center justify-center">...</span>
                </PaginationItem>
              )}
              <PaginationItem>
                <PaginationLink onClick={() => handlePageChange(totalPages)}>
                  {totalPages}
                </PaginationLink>
              </PaginationItem>
            </>
          )}

          <PaginationItem>
            <PaginationNext 
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    );
  };

  return (
    <AdminLayout title="Bookings">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Service Bookings</h2>
          <div className="flex items-center gap-4">
            <div className="text-sm text-muted-foreground">
              {totalItems > 0 && (
                <span>
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalItems)} of {totalItems} bookings
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-4 mb-4">
          {/* Status Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-1 bg-white">
                <Filter className="h-4 w-4" />
                <span>Status: {statusFilter === 'all' ? 'All' : statusFilter}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 bg-white border shadow-md">
              <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
              <DropdownMenuSeparator />
               <DropdownMenuCheckboxItem 
                checked={statusFilter === 'all'}
                onCheckedChange={() => handleStatusFilterChange('all')}
              >
                All Statuses
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem 
                checked={statusFilter === 'new'}
                onCheckedChange={() => handleStatusFilterChange('new')}
              >
                New
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem 
                checked={statusFilter === 'accepted'}
                onCheckedChange={() => handleStatusFilterChange('accepted')}
              >
                Accepted
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem 
                checked={statusFilter === 'completed'}
                onCheckedChange={() => handleStatusFilterChange('completed')}
              >
                Completed
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem 
                checked={statusFilter === 'cancelled'}
                onCheckedChange={() => handleStatusFilterChange('cancelled')}
              >
                Cancelled
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem 
                checked={statusFilter === 'declined'}
                onCheckedChange={() => handleStatusFilterChange('declined')}
              >
                Declined
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem 
                checked={statusFilter === 'blocked'}
                onCheckedChange={() => handleStatusFilterChange('blocked')}
              >
                Blocked
              </DropdownMenuCheckboxItem>
               <DropdownMenuCheckboxItem 
                 checked={statusFilter === 'travel'}
                 onCheckedChange={() => handleStatusFilterChange('travel')}
               >
                 Travel
               </DropdownMenuCheckboxItem>
               <DropdownMenuCheckboxItem 
                 checked={statusFilter === 'removed by admin'}
                 onCheckedChange={() => handleStatusFilterChange('removed by admin')}
               >
                 Removed by Admin
               </DropdownMenuCheckboxItem>
             </DropdownMenuContent>
           </DropdownMenu>
          
          {/* Service Filter */}
          <DropdownMenu open={isServiceDropdownOpen} onOpenChange={setIsServiceDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-1 bg-white">
                <Briefcase className="h-4 w-4" />
                <span>
                  Service: {serviceFilter === 'all' 
                    ? 'All' 
                    : servicesData?.find(s => s.id === serviceFilter)?.name?.substring(0, 15) + 
                      (servicesData?.find(s => s.id === serviceFilter)?.name?.length > 15 ? '...' : '') || 'Selected'}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 bg-white border shadow-md max-h-[300px] overflow-y-auto">
              <div className="p-2 border-b">
                <div className="flex items-center px-2 rounded-md border">
                  <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                  <Input 
                    className="flex h-10 border-0 bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Search services..." 
                    value={serviceSearchQuery}
                    onChange={(e) => setServiceSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              
              <DropdownMenuItem 
                className={serviceFilter === 'all' ? "bg-muted" : ""} 
                onClick={() => handleServiceFilterChange('all')}
              >
                All Services
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              
              {filteredServices.length > 0 ? (
                filteredServices.map(service => (
                  <DropdownMenuItem 
                    key={service.id} 
                    className={serviceFilter === service.id ? "bg-muted" : ""}
                    onClick={() => handleServiceFilterChange(service.id)}
                  >
                    {service.name}
                  </DropdownMenuItem>
                ))
              ) : (
                <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                  {serviceSearchQuery ? "No matching services" : "No services found"}
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Customer Name Filter */}
          <div className="flex items-center border rounded-md bg-white">
            <User className="h-4 w-4 ml-2 text-gray-500" />
            <Input
              placeholder="Filter by customer name..."
              value={customerFilter}
              onChange={handleCustomerFilterChange}
              className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-9"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader column="service_name">Service</SortableHeader>
              <SortableHeader column="customer_name">Customer</SortableHeader>
              <SortableHeader column="date">Date</SortableHeader>
              <TableHead>Time</TableHead>
              <SortableHeader column="status">Status</SortableHeader>
              <SortableHeader column="created_at">Created</SortableHeader>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  Loading bookings...
                </TableCell>
              </TableRow>
            ) : (
              currentBookings.map((booking) => {
                const formattedDateTime = formatDateTime(booking.date, booking.start_time);
                const customerDisplay = booking.status === 'blocked' ? 
                  <span className="text-gray-500">Time Block</span> :
                  booking.status === 'travel' ?
                  <span className="text-gray-500">Travel Block</span> :
                  <div className="flex items-center gap-1.5">
                    <span>{booking.customer_name}</span>
                    {booking.note && (
                      <FileText className="h-4 w-4 text-amber-500" aria-label="Has note" />
                    )}
                  </div>;
                
                return (
                  <TableRow 
                    key={booking.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleViewDetails(booking)}
                  >
                    <TableCell className="font-medium">{booking.service_name}</TableCell>
                    <TableCell>{customerDisplay}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        {formattedDateTime.date}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-gray-500" />
                        {formattedDateTime.time}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(booking.status)}</TableCell>
                    <TableCell>
                      {formatCreatedAt(booking.created_at)}
                    </TableCell>
                    <TableCell>
                      {booking.status !== 'removed by admin' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeBookingMutation.mutate(booking.id);
                          }}
                          disabled={removeBookingMutation.isPending}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Remove
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
            {!isLoading && (!bookings || bookings.length === 0) && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  No bookings found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {renderPagination()}
      </div>
    </AdminLayout>
  );
};

export default ManageBookings;
